import { useParams, Link } from "react-router-dom"
import { useQuery } from "@apollo/client"
import { gql } from "~/__generated__"
import { ChevronLeft } from "lucide-react"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { GraphqlError } from "~/ui/errors"
import { PlanDetailsQuery } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { useState } from "react"
import { JoinPlanModal } from "./join-plan-modal"
import { plansPath } from "~/common/paths"
import { cn } from "~/common/cn"

type PlanNode = Extract<NonNullable<PlanDetailsQuery["node"]>, { __typename: "Plan" }>

const isPlan = (node: any): node is PlanNode => {
  return node?.__typename === "Plan"
}

const PLAN_QUERY = gql(/* GraphQL */ `
  query PlanDetails($id: ID!) {
    node(id: $id) {
      ... on Plan {
        __typename
        id
        title
        description
        datetime
        createdAt
        minPeople
        maxPeople
        location {
          id
          name
          websiteUrl
          googleMapsUrl
          latitude
          longitude
        }
        creator {
          id
          fullName
        }
        planParticipations {
          edges {
            node {
              id
              guestsCount
              user {
                id
                firstName
                lastName
              }
            }
          }
        }
      }
    }
    viewer {
      id
    }
  }
`)

export const PlanDetailsScreen = () => {
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false)
  const { id } = useParams()
  const { data, error, loading } = useQuery(PLAN_QUERY, {
    variables: { id: id! },
  })

  if (loading) return null
  if (error) return <GraphqlError error={error} />

  const plan = data?.node
  if (!plan || !isPlan(plan)) return <div>Plan not found</div>

  const isUserParticipating: boolean = !!(
    data?.viewer?.id &&
    plan.planParticipations.edges.some((edge) => edge.node?.user.id === data.viewer?.id)
  )

  return (
    <TablePageLayout>
      {/* Back Link */}
      <Link
        to={plansPath({})}
        className={cn(
          "mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 hover:underline"
        )}
      >
        <ChevronLeft className="h-4 w-4" /> Back to Plans
      </Link>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{plan.title}</h1>
            <p className="mt-1 text-sm text-gray-500">Created by: {plan.creator.fullName}</p>
            {plan.description && <p className="mt-2 text-gray-600">{plan.description}</p>}
          </div>

          <div className="flex space-x-2">
            {data?.viewer?.id && (
              <Button
                variant={isUserParticipating ? "secondary" : "default"}
                onClick={() => setIsJoinModalOpen(true)}
              >
                {isUserParticipating ? "Leave Plan" : "Join Plan"}
              </Button>
            )}
            <Link to={`/plans/${id}/edit`}>
              <Button variant="outline">Edit Plan</Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Event Details</h2>
            <p>Date & Time: {new Date(plan.datetime).toLocaleString()}</p>
            <p>Created On: {new Date(plan.createdAt).toLocaleString()}</p>
            {plan.minPeople && <p>Minimum People: {plan.minPeople}</p>}
            {plan.maxPeople && <p>Maximum People: {plan.maxPeople}</p>}
          </div>

          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Location</h2>
            <p>{plan.location.name}</p>
            {plan.location.websiteUrl && (
              <p>
                <a
                  href={plan.location.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Website
                </a>
              </p>
            )}
            {plan.location.googleMapsUrl && (
              <p>
                <a
                  href={plan.location.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  View on Google Maps
                </a>
              </p>
            )}
            {plan.location.latitude && plan.location.longitude && (
              <p>
                Coordinates: {plan.location.latitude}, {plan.location.longitude}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <h2 className="mb-4 text-lg font-semibold">Participants</h2>
          {plan.planParticipations.edges.length > 0 ? (
            <div className="space-y-2">
              {plan.planParticipations.edges.map(({ node: participation }) => {
                if (!participation) return null

                return (
                  <div
                    key={participation.id}
                    className="flex items-center gap-2 rounded bg-gray-50 p-2"
                  >
                    <span className="font-medium">
                      {participation.user.firstName} {participation.user.lastName}
                    </span>
                    {participation.guestsCount > 0 && (
                      <span className="text-gray-600">
                        +{participation.guestsCount} guest
                        {participation.guestsCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-gray-600">No one has joined this plan yet.</p>
          )}
        </div>
      </div>
      <JoinPlanModal
        planId={plan.id}
        planTitle={plan.title}
        isOpen={isJoinModalOpen}
        onClose={() => setIsJoinModalOpen(false)}
        isJoined={isUserParticipating}
      />
    </TablePageLayout>
  )
}
